import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { IoIosSearch } from "react-icons/io";
import dayjs from "dayjs";
import { useDebounce } from "@/hooks/useDebounce";
import { useUserStore } from "@/store/userStore";
import { ColumnType, Input, Pagination, Table } from "@/components";
import { UserQuizDto } from "@/types";
import { KEYS_SEARCH_PARAMS } from ".";
import { filterParams } from "@/utils/filterParams";

export const Quizes = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const params = Object.fromEntries([...searchParams]);
  const { currentUser, fetchUserQuizzes, userQuizzes, setUserQuizzes } = useUserStore();

  const isInitialMount = useRef(true);
  const [inputValue, setInputValue] = useState<string>(
    params[KEYS_SEARCH_PARAMS.SEARCH] || "",
  );

  const currentPage = Number(params[KEYS_SEARCH_PARAMS.PAGE]) || 1;
  const search = params[KEYS_SEARCH_PARAMS.SEARCH] || "";
  const debouncedSearchTerm = useDebounce(inputValue, 500);

  const updateSearchParams = (object: { [key: string]: string }) => {
    const inputParams: { [key: string]: string } = params;
    for (const key in object) {
      const value = object[key];
      if (value) {
        inputParams[key] = value;
      } else {
        delete inputParams[key];
      }
    }
    setSearchParams(inputParams, { replace: true });
  };

  useEffect(() => {
    return () => {
      setUserQuizzes({ data: null, status: 200 });
    };
  }, []);

  useEffect(() => {
    if (currentUser?.id) {
      fetchUserQuizzes(Number(currentUser?.id), filterParams({
        s: search,
        page: currentPage,
      }));
    }
  }, [currentPage, search]);

  useEffect(() => {
    // Skip updating search params on initial mount
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    if (debouncedSearchTerm?.trim()) {
      updateSearchParams({
        [KEYS_SEARCH_PARAMS.SEARCH]: debouncedSearchTerm?.trim(),
        [KEYS_SEARCH_PARAMS.PAGE]: "1",
      });
    } else {
      updateSearchParams({ [KEYS_SEARCH_PARAMS.SEARCH]: "" });
    }
  }, [debouncedSearchTerm]);

  const learningColumns: ColumnType<UserQuizDto>[] = [
    {
      title: <p className="pl-2">テスト名</p>,
      dataIndex: "quiz",
      key: "title",
      render: (_, record) => (
        <p className="truncate pl-2">{record?.quiz?.title || "-"}</p>
      ),
    },
    {
      title: "日時",
      dataIndex: "updated_at",
      key: "updated_at",
      width: "15%",
      align: "center",
      className: "text-center",
      render: (value: string) => (
        <p>{value ? dayjs(value).format("YYYY/MM/DD") : "-"}</p>
      ),
    },
    {
      title: "スコア",
      dataIndex: "score",
      key: "score",
      width: "15%",
      align: "center",
      className: "text-center",
      render: (value: string) => <p>{value || "-"}</p>,
    },
  ];

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      updateSearchParams({ [KEYS_SEARCH_PARAMS.SEARCH]: inputValue });
    }
  };

  return (
    <section className="space-y-4 relative w-full h-full">
      <Input
        value={inputValue}
        onChange={(e) => setInputValue(e.target.value)}
        placeholder="検索"
        icon={<IoIosSearch className="size-4" />}
        className="!h-9 w-80"
        onKeyDown={handleKeyDown}
      />
      <div className="h-[32.8rem]">
        {userQuizzes?.data === null ? (
          <QuizPlaceHolder />
        ) : (
          <Table<UserQuizDto>
            columns={learningColumns}
            className="shadow-shadow_light_card"
            dataSource={userQuizzes?.data || []}
            bordered={true}
            rowKey="id"
          />
        )}
      </div>
      <Pagination
        currentPage={currentPage}
        totalPages={userQuizzes?.pagination?.pageCount || 0}
        onPageChange={(page: number) => {
          updateSearchParams({ [KEYS_SEARCH_PARAMS.PAGE]: page.toString() });
        }}
        className="mt-4"
      />
    </section>
  );
};

const QuizPlaceHolder = () => {
  const learningColumns: ColumnType[] = [
    {
      title: (
        <p className="pl-2 h-2 rounded-full w-52 bg-grey-300 animate-pulse"></p>
      ),
      dataIndex: "quiz",
      key: "quiz",
      render: () => (
        <p className="pl-2 h-2 rounded-full w-24 bg-grey-300 animate-pulse"></p>
      ),
    },
    {
      title: (
        <p className="pl-2 h-2 rounded-full w-12 bg-grey-300 animate-pulse"></p>
      ),
      dataIndex: "date",
      key: "date",
      width: "15%",
      render: () => (
        <p className="pl-2 h-2 rounded-full w-24 bg-grey-300 animate-pulse"></p>
      ),
    },
    {
      title: (
        <p className="pl-2 h-2 rounded-full w-12 bg-grey-300 animate-pulse"></p>
      ),
      dataIndex: "score",
      key: "score",
      width: "15%",
      render: () => (
        <p className="pl-2 h-2 rounded-full w-24 bg-grey-300 animate-pulse"></p>
      ),
    },
  ];
  return (
    <Table
      columns={learningColumns}
      className="shadow-shadow_light_card"
      dataSource={Array(12).fill({})}
      bordered={true}
      rowKey="id"
      onRow={() => ({
        style: {
          backgroundColor: "transparent",
          hover: {
            backgroundColor: "transparent",
          },
        },
      })}
    />
  );
};
